/**
 * portalRegistry — the SINGLE generated source for portal modules + navigation.
 *
 * Generated by codaic portal_generator (Portal v2, DEC-063). Do not hand-edit
 * structure by hand unless the portal is codegen-exempt; `codaic add service`
 * and `codaic init-wiki` re-emit / patch this file.
 *
 * Everything else derives from here at runtime: sidebar tree (Navigation),
 * horizontal SectionSubTabs, breadcrumb labels, module colors, search index.
 */

import React from 'react';
import { Box as BoxIcon, Settings, BookMarked, Compass } from 'lucide-react';

// ── Types ──────────────────────────────────────────────────────────────────────

export type ModuleId = 'transformation' | 'transformation-plan' | 'wiki';

export interface EntityRef {
  name: string;
  label: string;
  endpoint: string;
  hasTimestamps: boolean;
}

export interface ModuleConfig {
  id: ModuleId;
  label: string;
  accent: string;
  icon: string;
  description: string;
  /** Entity metadata for overview widgets (P2): CRUD list endpoints per module. */
  entities: EntityRef[];
}

export interface NavigationItem {
  label: string;
  path: string;
  children?: NavigationItem[];
}

export interface NavigationSection {
  id: string;
  label: string;
  accent: string;
  iconName: string;
  children: NavigationItem[];
}

// ── Modules ────────────────────────────────────────────────────────────────────

export const MODULE_CONFIGS: Record<ModuleId, ModuleConfig> = {
  'transformation': {
    id: 'transformation',
    label: 'Transformation',
    accent: '#4fc3f7',
    icon: 'Box',
    description: 'Transformation entities',
    entities: [{"name": "Comment", "endpoint": "comments", "label": "Comment", "hasTimestamps": true}, {"name": "Decision", "endpoint": "decisions", "label": "Decision", "hasTimestamps": true}, {"name": "Function", "endpoint": "functions", "label": "Function", "hasTimestamps": true}, {"name": "Initiative", "endpoint": "initiatives", "label": "Initiative", "hasTimestamps": true}, {"name": "Issue", "endpoint": "issues", "label": "Issue", "hasTimestamps": true}, {"name": "Kpi", "endpoint": "kpis", "label": "Kpi", "hasTimestamps": true}, {"name": "KpiValue", "endpoint": "kpi-values", "label": "Kpi Value", "hasTimestamps": true}, {"name": "Objective", "endpoint": "objectives", "label": "Objective", "hasTimestamps": true}, {"name": "Snapshot", "endpoint": "snapshots", "label": "Snapshot", "hasTimestamps": true}, {"name": "TaskEntry", "endpoint": "task-entries", "label": "Task Entry", "hasTimestamps": true}, {"name": "UpdateLog", "endpoint": "update-logs", "label": "Update Log", "hasTimestamps": true}, {"name": "WeeklyReport", "endpoint": "weekly-reports", "label": "Weekly Report", "hasTimestamps": true}],
  },
  'transformation-plan': {
    id: 'transformation-plan',
    label: 'Transformation Plan',
    accent: '#1D7FC2',
    icon: 'Compass',
    description: 'PDR surfaces — CEO view, PMO control room, function workspaces, board pack',
    entities: [],
  },
  'wiki': {
    id: 'wiki',
    label: 'Knowledge Wiki',
    accent: '#8b5cf6',
    icon: 'BookMarked',
    description: 'LLM-maintained knowledge base — ingest, query, lint',
    entities: [],
  },
};

export const MODULE_LIST: ModuleConfig[] = Object.values(MODULE_CONFIGS);

// ── Navigation tree ────────────────────────────────────────────────────────────

export const navigationConfig: NavigationSection[] = [
  {
    "id": "transformation-plan",
    "label": "Transformation Plan",
    "accent": "#1D7FC2",
    "iconName": "Compass",
    "children": [
      {
        "label": "CEO View",
        "path": "/ceo"
      },
      {
        "label": "PMO Control Room",
        "path": "/pmo"
      },
      {
        "label": "Board Pack",
        "path": "/board-pack"
      },
      {
        "label": "Meeting Mode",
        "path": "/meeting"
      },
      {
        "label": "Activity",
        "path": "/activity"
      },
      {
        "label": "Functions",
        "path": "/f",
        "children": [
          { "label": "DRL", "path": "/f/DRL" },
          { "label": "MNT", "path": "/f/MNT" },
          { "label": "PRC", "path": "/f/PRC" },
          { "label": "HSE", "path": "/f/HSE" },
          { "label": "CON", "path": "/f/CON" },
          { "label": "BDV", "path": "/f/BDV" },
          { "label": "HCM", "path": "/f/HCM" },
          { "label": "FIN", "path": "/f/FIN" },
          { "label": "AIT", "path": "/f/AIT" },
          { "label": "LGL", "path": "/f/LGL" },
          { "label": "LOG", "path": "/f/LOG" }
        ]
      }
    ]
  },
  {
    "id": "transformation",
    "label": "Transformation",
    "accent": "#4fc3f7",
    "iconName": "Box",
    "children": [
      {
        "label": "Overview",
        "path": "/transformation"
      },
      {
        "label": "Dashboard",
        "path": "/transformation/dashboard"
      },
      {
        "label": "Entities",
        "path": "/transformation/admin",
        "children": [
          {
            "label": "Comment",
            "path": "/transformation/admin/comments"
          },
          {
            "label": "Decision",
            "path": "/transformation/admin/decisions"
          },
          {
            "label": "Function",
            "path": "/transformation/admin/functions"
          },
          {
            "label": "Initiative",
            "path": "/transformation/admin/initiatives"
          },
          {
            "label": "Issue",
            "path": "/transformation/admin/issues"
          },
          {
            "label": "Kpi",
            "path": "/transformation/admin/kpis"
          },
          {
            "label": "Kpi Value",
            "path": "/transformation/admin/kpi-values"
          },
          {
            "label": "Objective",
            "path": "/transformation/admin/objectives"
          },
          {
            "label": "Snapshot",
            "path": "/transformation/admin/snapshots"
          },
          {
            "label": "Task Entry",
            "path": "/transformation/admin/task-entries"
          },
          {
            "label": "Update Log",
            "path": "/transformation/admin/update-logs"
          },
          {
            "label": "Weekly Report",
            "path": "/transformation/admin/weekly-reports"
          }
        ]
      }
    ]
  },
  {
    "id": "wiki",
    "label": "Knowledge Wiki",
    "accent": "#8b5cf6",
    "iconName": "BookMarked",
    "children": [
      {
        "label": "Index",
        "path": "/wiki"
      },
      {
        "label": "Ingest Source",
        "path": "/wiki/ingest"
      },
      {
        "label": "Lint Report",
        "path": "/wiki/lint"
      }
    ]
  },
  {
    "id": "system",
    "label": "System",
    "accent": "#64748b",
    "iconName": "Settings",
    "children": [
      {
        "label": "Home",
        "path": "/"
      },
      {
        "label": "Dashboard",
        "path": "/dashboard"
      },
      {
        "label": "Event Monitor",
        "path": "/events"
      },
      {
        "label": "Forge Admin",
        "path": "/forge/admin"
      },
      {
        "label": "Settings",
        "path": "/settings"
      }
    ]
  }
];

// ── Section icons ──────────────────────────────────────────────────────────────

export const SECTION_ICONS: Record<string, React.ReactElement> = {
  'transformation': <BoxIcon size={16} />,
  'transformation-plan': <Compass size={16} />,
  'system': <Settings size={16} />,
  'wiki': <BookMarked size={16} />,
};

// ── Derivations ────────────────────────────────────────────────────────────────

const PATH_LABELS: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  const walk = (items: NavigationItem[]) => {
    for (const it of items) {
      map[it.path] = it.label;
      if (it.children) walk(it.children);
    }
  };
  for (const s of navigationConfig) walk(s.children);
  return map;
})();

/** Exact-path label lookup, derived from the nav tree (no hand-kept name map). */
export function labelForPath(path: string): string | undefined {
  return PATH_LABELS[path];
}

/** Section owning the given path — deepest matching prefix wins. '/' matches exactly. */
export function findSectionForPath(path: string): NavigationSection | undefined {
  let best: NavigationSection | undefined;
  let bestLen = -1;
  const consider = (section: NavigationSection, p: string) => {
    if (p === '/') {
      if (path === '/' && bestLen < 0) {
        best = section;
        bestLen = 0;
      }
      return;
    }
    if ((path === p || path.startsWith(p + '/')) && p.length > bestLen) {
      best = section;
      bestLen = p.length;
    }
  };
  const walk = (section: NavigationSection, items: NavigationItem[]) => {
    for (const it of items) {
      consider(section, it.path);
      if (it.children) walk(section, it.children);
    }
  };
  for (const s of navigationConfig) walk(s, s.children);
  return best;
}

export interface FlatSearchEntry {
  label: string;
  path: string;
  section: string;
}

/** Flattened nav entries — ready for a command palette. */
export function buildSearchIndex(): FlatSearchEntry[] {
  const out: FlatSearchEntry[] = [];
  const walk = (sectionLabel: string, items: NavigationItem[]) => {
    for (const it of items) {
      if (it.children?.length) walk(sectionLabel, it.children);
      else out.push({ label: it.label, path: it.path, section: sectionLabel });
    }
  };
  for (const s of navigationConfig) walk(s.label, s.children);
  return out;
}
